import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { ROUTES } from '@/constants/routes';
import Navbar from '@/pages/site/home-page/components/navbar';

import { ErrorBoundary } from '../ui/error-boundary';
import Footer from './footer-section';

const SiteChatWidgetsLayer = lazy(() =>
  import('./site-chat-layer').then(module => ({
    default: module.SiteChatWidgetsLayer,
  }))
);
const SiteChatBoundary = lazy(() =>
  import('./site-chat-layer').then(module => ({
    default: module.SiteChatBoundary,
  }))
);

interface NavigationShellContentProps {
  currentPath: string;
}

const REGIS_SUPPRESSED_ROUTES = [
  ROUTES.SIGNUP,
  ROUTES.CHINOBI_SIGNUP,
  ROUTES.MEET_LIVE_AI,
] as const;

const IMMEDIATE_SITE_CHAT_ROUTES = [
  ROUTES.SEE_IT_RUN,
  ROUTES.MEET_LIVE_AI,
] as const;

const isOneOf = (currentPath: string, routes: readonly string[]) =>
  routes.includes(currentPath);

const isAgentOrBlogRoute = (currentPath: string) =>
  currentPath.startsWith(ROUTES.AGENTS_DETAILS('')) ||
  currentPath.startsWith(ROUTES.BLOG);

const isImmediateSiteChatContentRoute = (currentPath: string) =>
  isAgentOrBlogRoute(currentPath) || currentPath.startsWith(ROUTES.PRICING);

const shouldSuppressRegisBar = (currentPath: string) =>
  isOneOf(currentPath, REGIS_SUPPRESSED_ROUTES) ||
  isAgentOrBlogRoute(currentPath);

const needsImmediateSiteChatContext = (currentPath: string) =>
  isOneOf(currentPath, IMMEDIATE_SITE_CHAT_ROUTES) ||
  isImmediateSiteChatContentRoute(currentPath);

const preloadCommonPublicRoutes = () => {
  void import('@/pages/site/contact-page');
  void import('@/pages/site/integrations-page');
  void import('@/pages/site/pricing-page');
};

const NavigationShellContent = ({
  currentPath,
}: NavigationShellContentProps) => {
  // Auth pages render their own minimal nav, and `/see-it-run`
  // embeds the navbar inside its sticky scroll-jacked hero so the bar
  // stays pinned through the horizontal scroll (the global navbar is
  // `position: absolute` and would scroll away with the document).
  // Everywhere else the shell renders the navbar.
  const noShellNav =
    currentPath === ROUTES.LOGIN || currentPath === ROUTES.SEE_IT_RUN;

  // Auth pages are full-screen takeovers — no public-site footer.
  const noShellFooter =
    currentPath === ROUTES.SIGNUP ||
    currentPath === ROUTES.CHINOBI_SIGNUP ||
    currentPath === ROUTES.LOGIN;

  // Pages with a light background — switch the navbar to its light
  // theme (black text + brand logo) so it's legible. Hero pages keep
  // the default dark theme.
  const lightNavRoutes = [ROUTES.PRICING, ROUTES.BLOG_DETAILS('')];
  const isTrackArticleRoute = [ROUTES.BLOG_LEDGER, ROUTES.BLOG_INFERENCE].some(
    route => currentPath.startsWith(`${route}/`)
  );
  const navbarTheme: 'light' | 'dark' =
    lightNavRoutes.some(p => currentPath.startsWith(p)) || isTrackArticleRoute
      ? 'light'
      : 'dark';
  const hideGetStarted = currentPath === ROUTES.SIGNUP;

  return (
    <div className="flex min-h-screen flex-col">
      {!noShellNav && (
        <Navbar
          theme={navbarTheme}
          hideGetStarted={hideGetStarted}
          containerClassName={
            currentPath === ROUTES.CHINOBI_SIGNUP
              ? 'mx-auto w-[calc(90%-34px)] md:w-[calc(90%-58px)] lg:w-[calc(90%-106px)] lg:max-w-[1450px]'
              : undefined
          }
        />
      )}
      <main className="flex-1">
        <Outlet />
      </main>
      {!noShellFooter && <Footer />}
    </div>
  );
};

const DeferredSiteChatLayer = ({
  suppressRegisBar,
}: {
  suppressRegisBar: boolean;
}) => {
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setShouldMount(true), 1200);
    return () => window.clearTimeout(timeout);
  }, []);

  if (!shouldMount) return null;

  return (
    <Suspense fallback={null}>
      <SiteChatWidgetsLayer suppressRegisBar={suppressRegisBar} />
    </Suspense>
  );
};

const NavigationShell = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isAuthPage =
    currentPath === ROUTES.LOGIN ||
    currentPath === ROUTES.SIGNUP ||
    currentPath === ROUTES.CHINOBI_SIGNUP;
  const suppressRegisBar = shouldSuppressRegisBar(currentPath);

  useEffect(() => {
    if (isAuthPage) return;

    const schedule =
      'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (callback: IdleRequestCallback) =>
            window.setTimeout(() => callback({} as IdleDeadline), 1500);
    const cancel =
      typeof window.cancelIdleCallback === 'function'
        ? window.cancelIdleCallback
        : window.clearTimeout;

    const id = schedule(preloadCommonPublicRoutes, { timeout: 2500 });
    return () => cancel(id);
  }, [isAuthPage]);

  if (isAuthPage) {
    return (
      <ErrorBoundary>
        <NavigationShellContent currentPath={currentPath} />
      </ErrorBoundary>
    );
  }

  if (needsImmediateSiteChatContext(currentPath)) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <SiteChatBoundary suppressRegisBar={suppressRegisBar}>
            <NavigationShellContent currentPath={currentPath} />
          </SiteChatBoundary>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <NavigationShellContent currentPath={currentPath} />
      <DeferredSiteChatLayer suppressRegisBar={suppressRegisBar} />
    </ErrorBoundary>
  );
};

export default NavigationShell;
